import { TFunction } from "i18next";
import { get, set } from "lodash";
import {
  FieldErrors,
  FieldValues,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
} from "react-hook-form";

import {
  BULK_FORM_NAME_PREFIX,
  BulkMultiselectActionsEnum,
  controlNameMetadataResolve,
  doesBulkActionRequireSelectedValues,
  getBulkFormFieldName,
  ICustomField,
  isBulkMultiselectValueEmpty,
  RydeUiUnit,
  unique,
} from "@ryde/shared";

import {
  doesControlRequireBulkAction,
  isMultiselectBulkFieldType,
} from "../layout-edit/form.controls.bulk.utils";

export const isBulkActionFieldName = (fieldName: string): boolean =>
  fieldName.endsWith(`_${BULK_FORM_NAME_PREFIX}`);

const isMainFieldSelectedForBulk = ({
  control,
  selectedFieldsSet,
}: {
  control: ICustomField;
  selectedFieldsSet: Set<string>;
}): boolean => {
  const resolvedName = controlNameMetadataResolve(control);

  return selectedFieldsSet.has(resolvedName);
};

export const getBulkActionFieldNamesToValidate = ({
  selectedFields,
  controls,
  entity,
}: {
  selectedFields: string[];
  controls: ICustomField[];
  entity: RydeUiUnit;
}): string[] => {
  const selectedFieldsSet = new Set(selectedFields);

  return unique(
    controls
      .filter(control =>
        isMainFieldSelectedForBulk({ control, selectedFieldsSet }),
      )
      .filter(control => doesControlRequireBulkAction({ control, entity }))
      .map(control =>
        getBulkFormFieldName(controlNameMetadataResolve(control)),
      ),
  );
};

export const getSelectedFieldsToTrigger = ({
  selectedFields,
  controls,
  entity,
}: {
  selectedFields: string[];
  controls: ICustomField[];
  entity: RydeUiUnit;
}): string[] =>
  unique([
    ...selectedFields,
    ...getBulkActionFieldNamesToValidate({
      selectedFields,
      controls,
      entity,
    }),
  ]);

export const validateSelectedBulkActionFields = <T extends FieldValues>({
  selectedFields,
  controls,
  entity,
  getValues,
  setError,
  clearErrors,
  t,
}: {
  selectedFields: string[];
  controls: ICustomField[];
  entity: RydeUiUnit;
  getValues: UseFormGetValues<T>;
  setError: UseFormSetError<T>;
  clearErrors: UseFormClearErrors<T>;
  t: TFunction;
}): boolean => {
  const bulkActionFieldNames = getBulkActionFieldNamesToValidate({
    selectedFields,
    controls,
    entity,
  });

  if (!bulkActionFieldNames.length) {
    return true;
  }

  let isValid = true;
  const requiredMessage = t("errors.requiredField", {
    fieldName: t("general.multiselectBulkAction"),
  });

  bulkActionFieldNames.forEach(fieldName => {
    const value: unknown = getValues(fieldName as never);
    const isEmpty = value === undefined || value === null || value === "";

    if (isEmpty) {
      setError(fieldName as never, {
        type: "required",
        message: requiredMessage,
      });
      isValid = false;
      return;
    }

    clearErrors(fieldName as never);
  });

  return isValid;
};

/**
 * ADD/REMOVE must name at least one value; empty arrays would otherwise hit the API
 * and fail only after the process starts.
 */
export const validateSelectedBulkMultiselectValues = <T extends FieldValues>({
  selectedFields,
  controls,
  entity,
  getValues,
  setError,
  clearErrors,
  t,
}: {
  selectedFields: string[];
  controls: ICustomField[];
  entity: RydeUiUnit;
  getValues: UseFormGetValues<T>;
  setError: UseFormSetError<T>;
  clearErrors: UseFormClearErrors<T>;
  t: TFunction;
}): boolean => {
  const selectedFieldsSet = new Set(selectedFields);
  let isValid = true;

  controls
    .filter(control =>
      isMainFieldSelectedForBulk({ control, selectedFieldsSet }),
    )
    .filter(control => doesControlRequireBulkAction({ control, entity }))
    .filter(control => isMultiselectBulkFieldType(control.formFieldTypeId))
    .forEach(control => {
      const fieldName = controlNameMetadataResolve(control);
      const bulkAction: unknown = getValues(
        getBulkFormFieldName(fieldName) as never,
      );

      if (
        !doesBulkActionRequireSelectedValues(
          bulkAction as BulkMultiselectActionsEnum | string | undefined,
        )
      ) {
        clearErrors(fieldName as never);
        return;
      }

      const value = getValues(fieldName as never);

      if (isBulkMultiselectValueEmpty(value)) {
        setError(fieldName as never, {
          type: "required",
          message: t("errors.requiredField", {
            fieldName: control.formLabel || fieldName,
          }),
        });
        isValid = false;
        return;
      }

      clearErrors(fieldName as never);
    });

  return isValid;
};

export const pickSelectedBulkFormValues = <T extends FieldValues>(
  formValues: T,
  selectedFields: string[],
): Partial<T> => {
  return selectedFields.reduce((acc, fieldName) => {
    set(acc, fieldName, get(formValues, fieldName));
    return acc;
  }, {} as Partial<T>);
};

export const collectFormErrorPaths = (
  formErrors: FieldErrors | undefined,
  parentPath = "",
): string[] => {
  if (!formErrors) {
    return [];
  }

  return Object.keys(formErrors).flatMap(key => {
    const error = formErrors[key];
    const currentPath = parentPath ? `${parentPath}.${key}` : key;

    if (!error || typeof error !== "object") {
      return [];
    }

    if ("message" in error && error.message) {
      return [currentPath];
    }

    return collectFormErrorPaths(error as FieldErrors, currentPath);
  });
};

export const clearErrorsOutsideSelectedFields = <T extends FieldValues>({
  formErrors,
  selectedFields,
  clearErrors,
}: {
  formErrors: FieldErrors | undefined;
  selectedFields: string[];
  clearErrors: UseFormClearErrors<T>;
}): void => {
  const selectedFieldsSet = new Set(selectedFields);

  collectFormErrorPaths(formErrors)
    .filter(path => !selectedFieldsSet.has(path))
    .forEach(path => {
      clearErrors(path as never);
    });
};
